import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Person } from "../rounds/PersonPicker";
import { matchRoster, searchWorkersOnline } from "../punches/roster";
import type { AttendanceRow } from "../punches/attendanceRows";
import SearchField from "../ui/SearchField";
import { useKT } from "../i18n/kt";

/**
 * Someone walks in: find them first (anyone in Workers, by code or name), then add them only if
 * they aren't there. With nothing typed, the regulars who haven't come yet are offered.
 * A sheet from the bottom on phones; docked beside the list on bigger screens.
 */
const FindOrAddPerson = ({
  roster,
  rows,
  isToday,
  docked = false,
  busyId,
  onIn,
  onOpen,
  onAdd,
  onClose,
}: {
  roster: Person[];
  /** Today's attendance rows: who is already in, out, or not in yet. */
  rows: AttendanceRow[];
  isToday: boolean;
  docked?: boolean;
  busyId: string | null;
  onIn: (p: Person, row?: AttendanceRow) => void;
  onOpen: (p: Person) => void;
  onAdd: (name: string) => void;
  onClose?: () => void;
}) => {
  const { t } = useKT();
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const rowById = useMemo(() => new Map(rows.map((r) => [r.personId, r])), [rows]);

  useEffect(() => {
    // The sheet opens to type straight away; a docked panel waits to be tapped.
    if (!docked) box.current?.querySelector("input")?.focus();
  }, [docked]);

  const term = q.trim();
  const local: Person[] = useMemo(
    () =>
      term
        ? matchRoster(roster, term, 8)
        : rows.filter((r) => r.kind === "NOT_YET").map((r) => ({ id: r.personId, code: r.code, name: r.name })),
    [roster, rows, term]
  );
  // The phone's copy can miss someone added elsewhere: ask Workers before offering "Add".
  const [online, setOnline] = useState<{ term: string; people: Person[] } | null>(null);
  const missLocally = Boolean(term) && !local.length;
  useEffect(() => {
    if (!missLocally) return;
    let live = true;
    const timer = window.setTimeout(() => {
      searchWorkersOnline(term).then((people) => {
        if (live) setOnline({ term, people });
      });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [missLocally, term]);
  const checking = missLocally && online?.term !== term;
  const found: Person[] = missLocally ? (online?.term === term ? online.people : []) : local;
  const looksLikeCode = /^[A-Za-z]{1,4}[_-]?\d+$/.test(term);

  const state = (p: Person) => {
    const r = rowById.get(p.id);
    if (!r || r.kind === "NOT_YET") return { text: r?.lastDay ? t("Not in yet") : t("Not in today"), canIn: true, r };
    if (r.kind === "IN") return { text: t("In since {{time}}", { time: r.since || r.firstIn || "" }), canIn: false, r };
    if (r.kind === "OUT") return { text: t("Out {{time}}", { time: r.lastOut || "" }), canIn: true, r };
    return { text: t("Missing an In or Out"), canIn: false, r };
  };

  const body = (
    <div className="kfa" ref={box}>
      <SearchField value={q} onChange={setQ} placeholder={t("Name or code, e.g. J_12")} label={t("Find someone")} autoCapitalize="characters" />
      {!term && found.length ? <span className="kfa__hint">{t("Not in yet today")}</span> : null}
      {!term && !found.length ? <span className="kfa__hint">{t("Type a name or code. If they aren't in Workers yet, add them here.")}</span> : null}
      {found.length ? (
        <ul className="kfa__list">
          {found.map((p) => {
            const s = state(p);
            return (
              <li key={p.id}>
                <button type="button" className="kfa__who" onClick={() => onOpen(p)}>
                  <strong>
                    <span className="kw-code">{p.code}</span> {p.name}
                  </strong>
                  <span>{s.text}</span>
                </button>
                {isToday && s.canIn ? (
                  <button type="button" className="kd-btn kd-btn--sm kat-act" disabled={busyId === p.id} onClick={() => onIn(p, s.r)}>
                    {t("In")}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {checking ? <span className="kfa__hint">{t("Looking in Workers…")}</span> : null}
      {term && !checking && !found.length ? <span className="kfa__hint">{t("No one called “{{name}}” in Workers.", { name: term })}</span> : null}
      {term && !checking && !looksLikeCode ? (
        <button type="button" className={`kfa__add${found.length ? "" : " is-main"}`} onClick={() => onAdd(term)}>
          + {t("Add “{{name}}” as a new person", { name: term })}
        </button>
      ) : null}
      {!term ? (
        <button type="button" className="kfa__add" onClick={() => onAdd("")}>
          + {t("New person, not in Workers yet")}
        </button>
      ) : null}
    </div>
  );

  if (docked) {
    return (
      <section className="kfa-panel" aria-label={t("Find or add someone")}>
        <h3>{t("Who came?")}</h3>
        {body}
      </section>
    );
  }
  return (
    <>
      <div className="kd-backdrop" onClick={onClose} />
      <section className="kd-actions kfa-sheet" role="dialog" aria-modal="true" aria-label={t("Find or add someone")}>
        <div className="kd-actions__grab" aria-hidden="true" />
        <header className="kd-actions__head">
          <h3>{t("Who came?")}</h3>
          <span>{t("Find them first. Add only if they aren't there.")}</span>
        </header>
        {body}
        <button type="button" className="kd-btn kd-btn--line kd-actions__cancel" onClick={onClose}>
          {t("Cancel")}
        </button>
      </section>
    </>
  );
};

export default FindOrAddPerson;
